// Shared HTTP client for the Uben API.
// Handles session persistence, token expiry checks, single-flight refresh,
// and a single retry after a 401 refresh. Feature modules build on this.
import { jwtDecode } from 'jwt-decode'

export const SESSION_KEY = 'supabase_session'
export const API_BASE = '/api'

export class ApiError extends Error {
  constructor(message, { code, status } = {}) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
  }
}

export class ApiClient {
  constructor({ baseUrl = API_BASE, sessionKey = SESSION_KEY } = {}) {
    this.baseUrl = baseUrl
    this.sessionKey = sessionKey
    this.session = this.getStoredSession()
    this._refreshPromise = null
    this._listeners = []
  }

  onSessionChange(fn) {
    this._listeners.push(fn)
    return () => {
      this._listeners = this._listeners.filter((f) => f !== fn)
    }
  }

  _notify(event, session) {
    this._listeners.forEach((fn) => {
      try {
        fn(event, session)
      } catch (e) {
        console.error('Session listener error:', e)
      }
    })
  }

  getStoredSession() {
    const stored = localStorage.getItem(this.sessionKey)
    return stored ? JSON.parse(stored) : null
  }

  setSession(session) {
    this.session = session
    if (session) {
      localStorage.setItem(this.sessionKey, JSON.stringify(session))
    } else {
      localStorage.removeItem(this.sessionKey)
    }
    this._notify('SESSION_CHANGED', session)
  }

  _isTokenExpired(token) {
    if (!token) return true
    try {
      const decoded = jwtDecode(token)
      return !decoded.exp || decoded.exp <= Date.now() / 1000
    } catch {
      return true
    }
  }

  // Single-flight: concurrent callers share one in-flight refresh.
  _refresh() {
    if (this._refreshPromise) return this._refreshPromise
    this._refreshPromise = this._doRefresh().finally(() => {
      this._refreshPromise = null
    })
    return this._refreshPromise
  }

  async _doRefresh() {
    if (!this.session?.refresh_token) {
      this.setSession(null)
      return null
    }
    let response
    try {
      response = await fetch(`${this.baseUrl}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'refreshSession',
          refreshToken: this.session.refresh_token
        })
      })
    } catch (error) {
      console.error('Refresh request failed:', error)
      this.setSession(null)
      return null
    }
    const result = await response.json()
    if (!response.ok || !result.session) {
      this.setSession(null)
      return null
    }
    this.setSession(result.session)
    return result.session
  }

  async call(endpoint, options = {}) {
    const { _skipAuth = false, _retried = false, ...fetchOptions } = options

    let headers = { 'Content-Type': 'application/json', ...(fetchOptions.headers || {}) }

    if (!_skipAuth) {
      if (this.session?.access_token && this._isTokenExpired(this.session.access_token)) {
        await this._refresh()
      }
      if (this.session?.access_token) {
        headers.Authorization = `Bearer ${this.session.access_token}`
      }
    }

    let response
    try {
      response = await fetch(`${this.baseUrl}${endpoint}`, { ...fetchOptions, headers })
    } catch (error) {
      throw new ApiError(error.message || 'Network error', { code: 'NETWORK_ERROR' })
    }

    let result = {}
    try {
      result = await response.json()
    } catch {
      result = {}
    }

    if (!response.ok) {
      if (response.status === 401 && !_skipAuth && !_retried) {
        await this._refresh()
        if (this.session?.access_token) {
          return this.call(endpoint, { ...options, _retried: true })
        }
      }
      throw new ApiError(result.error || `Request failed (${response.status})`, {
        code: result.code,
        status: response.status
      })
    }

    return result
  }
}

export const apiClient = new ApiClient()
